import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Upload, Copy, ChevronRight, GitCompare } from "lucide-react";
import { runsApi } from "../api/endpoints";
import { SkeletonTable } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { formatDateTime, shortHash } from "../lib/format";
import { cn } from "../lib/utils";

function StatusChip({ status }) {
  const map = {
    completed: "bg-emerald-100 text-emerald-800",
    running: "bg-sky-100 text-sky-800",
    pending: "bg-slate-100 text-slate-700",
    failed: "bg-red-100 text-red-800",
  };
  return (
    <span
      data-testid={`run-status-${status}`}
      className={cn(
        "inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium",
        map[status] || "bg-slate-100 text-slate-700",
      )}
    >
      {status}
    </span>
  );
}

function HashCell({ value, testId }) {
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <span
        title={value}
        data-testid={testId}
        className="font-mono text-[11px] text-slate-700"
      >
        {shortHash(value)}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigator.clipboard?.writeText(value);
        }}
        aria-label={`Copy ${value}`}
        className="text-slate-400 hover:text-slate-800"
        data-testid={`${testId}-copy`}
      >
        <Copy size={11} />
      </button>
    </div>
  );
}

export default function AnalysesListPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [fromRunId, setFromRunId] = useState("");
  const [toRunId, setToRunId] = useState("");

  const load = () => {
    setError(null);
    setRuns(null);
    runsApi.list(workspaceId).then(setRuns).catch(setError);
  };
  useEffect(load, [workspaceId]);

  const completedRuns = useMemo(
    () => (runs || []).filter((r) => r.status === "completed"),
    [runs],
  );
  const canCompare = fromRunId && toRunId && fromRunId !== toRunId;
  const handleCompare = () => {
    if (!canCompare) return;
    navigate(`/w/${workspaceId}/analyses/${fromRunId}/compare/${toRunId}`);
  };

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Workspace
          </div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">
            Analyses
          </h1>
        </div>
        <Link
          to={`/w/${workspaceId}/import`}
          data-testid="analyses-new"
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Upload size={14} /> New analysis
        </Link>
      </header>

      {completedRuns.length >= 2 && (
        <div
          data-testid="compare-picker"
          className="rounded border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3"
        >
          <div className="flex flex-col gap-1">
            <label htmlFor="compare-from" className="text-[11px] uppercase tracking-wider text-slate-500">
              Compare from
            </label>
            <select
              id="compare-from"
              data-testid="compare-from-select"
              value={fromRunId}
              onChange={(e) => setFromRunId(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm min-w-[220px]"
            >
              <option value="">Pick a run…</option>
              {completedRuns.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {shortHash(r.id)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="compare-to" className="text-[11px] uppercase tracking-wider text-slate-500">
              Compare to
            </label>
            <select
              id="compare-to"
              data-testid="compare-to-select"
              value={toRunId}
              onChange={(e) => setToRunId(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm min-w-[220px]"
            >
              <option value="">Pick a run…</option>
              {completedRuns.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {shortHash(r.id)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            data-testid="compare-runs-btn"
            onClick={handleCompare}
            disabled={!canCompare}
            className={cn(
              "inline-flex items-center gap-2 rounded px-4 py-1.5 text-sm font-medium",
              canCompare
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : "bg-slate-100 text-slate-400 cursor-not-allowed",
            )}
          >
            <GitCompare size={14} /> Compare runs
          </button>
          <p className="text-[11px] text-slate-500 max-w-md">
            Deterministic diff — no AI. Same inputs always produce the same content hash.
          </p>
        </div>
      )}

      <div className="rounded border border-slate-200 bg-white overflow-hidden">
        {runs === null && !error ? (
          <div className="p-6">
            <SkeletonTable rows={5} cols={6} />
          </div>
        ) : error ? (
          <div className="p-6">
            <ErrorState error={error} onRetry={load} />
          </div>
        ) : runs.length === 0 ? (
          <EmptyState
            title="No analyses yet"
            description="Import a BIA workbook to run your first analysis."
            action={
              <Link
                to={`/w/${workspaceId}/import`}
                data-testid="analyses-empty-cta"
                className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Import BIAs
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table
              data-testid="analyses-table"
              className="w-full text-sm"
            >
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Created</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Engine</th>
                  <th className="px-4 py-2.5">Library</th>
                  <th className="px-4 py-2.5">Library hash</th>
                  <th className="px-4 py-2.5">Mapping</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`run-row-${r.id}`}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/w/${workspaceId}/analyses/${r.id}`}
                        className="font-medium text-slate-900 hover:underline"
                      >
                        {r.name}
                      </Link>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        by {r.created_by?.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {formatDateTime(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={r.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-700">
                      {r.engine_version || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-700">
                      {r.rule_library_version || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <HashCell value={r.rule_library_hash} testId={`rlh-${r.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      {r.mapping_content_hash ? (
                        <HashCell value={r.mapping_content_hash} testId={`mh-${r.id}`} />
                      ) : (
                        <span className="text-[11px] uppercase tracking-wider text-slate-400">
                          JSON ingest
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/w/${workspaceId}/analyses/${r.id}`}
                        className="inline-flex items-center text-slate-500 hover:text-slate-900"
                        aria-label={`Open ${r.name}`}
                      >
                        <ChevronRight size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
